import { Form, Select } from 'antd'
import { NamePath } from 'antd/es/form/interface'
import { FC, useCallback, useMemo } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { useGetMaterialsQuery } from '../../../../../../../store/api/materials-api'
import { requiredOneElementRules } from '../../../../../../../constants/forms/validation/rules'
import calculateStockAmount from '../../../../../../../helpers/entities/stock/calculate-stock-amount'
import useMaterialWidthOptions from '../../../../../../../helpers/render/use-material-width-options'
import { STANDARD_MATERIAL_WIDTH_MIN } from '../../../../../../../helpers/material-width'
import type MaterialEntity from '../../../../../../../types/api/material'
import { StrapiDataItem } from '../../../../../../../types/strapi/strapi'

interface MaterialWidthStockProps {
    materialTypeDocId: string
    name: NamePath
    rest?: { fieldKey?: number }
    /** Уже посчитанный минимум (ориентир − 10). Не применять getMinSelectableMaterialWidth повторно. */
    minMaterialWidth?: number
    /** Все ширины типа без minWidth и без фильтра стандартного ряда (доп. материалы). */
    unrestrictedWidths?: boolean
    disabled?: boolean
}

const MaterialWidthStock: FC<MaterialWidthStockProps> = ({
    materialTypeDocId,
    name,
    rest,
    minMaterialWidth,
    unrestrictedWidths = false,
    disabled,
}) => {
    const minWidth = unrestrictedWidths ? undefined : minMaterialWidth

    const queryArg = useMemo(() => {
        if (!materialTypeDocId) {
            return skipToken
        }

        return {
            filters: {
                material_type: { documentId: { $eq: materialTypeDocId } },
                ...(unrestrictedWidths
                    ? {}
                    : {
                          width: {
                              $gte: minWidth ?? STANDARD_MATERIAL_WIDTH_MIN,
                          },
                      }),
            },
            populate: {
                stocks: {
                    populate: {
                        stock_reserves: true,
                    },
                },
            },
            pagination: {
                page: 1,
                pageSize: 1000,
            },
        }
    }, [materialTypeDocId, minWidth, unrestrictedWidths])

    const { data: materialsData } = useGetMaterialsQuery(queryArg)

    const baseOptions = useMaterialWidthOptions<StrapiDataItem<MaterialEntity>>({
        items: materialsData?.data,
        getWidth: item => item.width,
        getDocumentId: item => item.documentId,
        getStock: item =>
            item.stocks?.reduce((sum, stock) => sum + calculateStockAmount(stock), 0) || 0,
        minWidth,
        standardOnly: !unrestrictedWidths,
    })

    const materialName = [name, 'material']

    const validateWidth = useCallback(
        (_: unknown, value: unknown) => {
            if (!value) {
                return Promise.resolve()
            }

            const selectedFromList = baseOptions.some(opt => String(opt.key) === String(value))

            if (selectedFromList) {
                return Promise.resolve()
            }

            return Promise.reject(new Error('Выберите ширину из списка'))
        },
        [baseOptions]
    )

    const filterOption = useCallback(
        (input: string, option?: { value?: string | number }) => {
            const opt = baseOptions.find(o => o.key === option?.value)
            return opt ? String(opt.value).includes(input.trim()) : false
        },
        [baseOptions]
    )

    return (
        <Form.Item
            name={materialName}
            label={'Ширина'}
            rules={[...requiredOneElementRules, { validator: validateWidth }]}
            {...rest}
        >
            <Select
                options={baseOptions.map(opt => ({
                    value: opt.key,
                    label: opt.label,
                }))}
                placeholder='Выберите ширину'
                style={{ width: '100%' }}
                allowClear
                disabled={disabled}
                showSearch
                filterOption={filterOption}
                dropdownStyle={{ minWidth: '250px' }}
            />
        </Form.Item>
    )
}

export default MaterialWidthStock
